import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, Loader2, CheckCircle, X } from "lucide-react";
import { toast } from "sonner";

export default function PasswordSetupBanner() {
  const { user } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [done, setDone] = useState(false);

  if (!user?.needs_password_setup || dismissed || done) return null;

  const save = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/set-password", { password });
      setDone(true);
      toast.success("Password set. You can now log in any time with your email + password.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="brutal-border-red bg-gradient-to-r from-[#FF3333]/10 via-[#0a0a0a] to-[#0a0a0a] p-6 mb-8 relative" data-testid="password-setup-banner">
      <button
        onClick={() => setDismissed(true)}
        data-testid="password-setup-dismiss"
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-white/40 hover:text-white"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-3 mb-3">
        <Lock className="w-5 h-5 text-[#FF3333]" />
        <div>
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#FF3333]">SET YOUR PASSWORD</div>
          <div className="font-heading uppercase text-lg mt-1">Secure your forged account</div>
        </div>
      </div>
      <p className="font-sub text-white/65 text-sm mb-4 max-w-2xl">
        We auto-created an account for {user.email} when you subscribed. Set a password so you can
        log back in any time on a new device.
      </p>
      <form onSubmit={save} className="flex flex-wrap items-stretch gap-3 max-w-2xl">
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="New password (8+ chars)"
          data-testid="password-setup-new"
          className="flex-1 min-w-[200px] bg-black border border-white/15 px-4 py-3 font-mono text-sm text-white placeholder-white/30 focus:border-[#FF3333] focus:outline-none"
        />
        <input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Confirm"
          data-testid="password-setup-confirm"
          className="flex-1 min-w-[200px] bg-black border border-white/15 px-4 py-3 font-mono text-sm text-white placeholder-white/30 focus:border-[#FF3333] focus:outline-none"
        />
        <button
          type="submit"
          disabled={saving}
          data-testid="password-setup-submit"
          className="btn-forged-red disabled:opacity-50"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving</> : <><CheckCircle className="w-4 h-4" /> Set Password</>}
        </button>
      </form>
    </div>
  );
}
